// Import the Environment interface and schema
import {Environment, envSchema} from "../config/environment.interface";

// Extend the Window interface to include RUNTIME_CONFIG
declare global {
  interface Window {
    RUNTIME_CONFIG?: {
      ENVIRONMENT?: string;
      AUTHENTICATION_PROVIDER?: string;
      AUTH0_ISSUER?: string;
      AUTH0_UI_URL?: string;
      AUTH0_STAFF_CONNECTION?: string;
      LOCAL_CLIENT_PREPARATION_ENABLED?: string;
      INTERNAL_ADMIN_EMAIL_DOMAINS?: string;
      SHINE_AI_API_URL?: string;
      SHINE_ENRICH_API_URL?: string;
      SHINE_AI_WEB_URL?: string;
        SHINE_CONNECT_API_URL?: string;
        SHINE_WEBINARS_URL?: string;
      SHINE_TRANSLATIONS_URL?: string;
      SHINE_UI_URL?: string;
      SHINE_AI_FRONTEND_URL?: string;
      SHINE_AUTH_EXTERNAL_URL?: string;
      HOSTING_URL?: string;
      WIDGET_SCRIPT_URL?: string;
      FRONTEND_DUMMY_URL?: string;
      LOG_LEVEL?: string;
      METRICS_ENABLED?: string;
      TRACING_ENABLED?: string;
      AZURE_AD_CLIENT_ID?: string;
      AZURE_AD_AUTHORITY?: string;
    };
  }
}

// Function to get runtime config from window.RUNTIME_CONFIG
export function getRuntimeConfig() {
  if (typeof window === 'undefined') {
    console.warn('Window is not defined, this should only happen during SSR');
    return getDefaultConfig();
  }

  if (!window.RUNTIME_CONFIG) {
    console.warn('Runtime config not found. Make sure config.js is loaded before the application.');
    return getDefaultConfig();
  }

  console.log('Using runtime configuration from config.js');

  // Map runtime config to environment schema
  return {
    NODE_ENV: window.RUNTIME_CONFIG.ENVIRONMENT || 'development',
    AUTHENTICATION_PROVIDER: window.RUNTIME_CONFIG.AUTHENTICATION_PROVIDER || 'entra',
    AUTH0_ISSUER: window.RUNTIME_CONFIG.AUTH0_ISSUER || '',
    AUTH0_UI_URL: window.RUNTIME_CONFIG.AUTH0_UI_URL || '',
    AUTH0_STAFF_CONNECTION: window.RUNTIME_CONFIG.AUTH0_STAFF_CONNECTION || '',
    LOCAL_CLIENT_PREPARATION_ENABLED: window.RUNTIME_CONFIG.LOCAL_CLIENT_PREPARATION_ENABLED ?? 'true',
    INTERNAL_ADMIN_EMAIL_DOMAINS: window.RUNTIME_CONFIG.INTERNAL_ADMIN_EMAIL_DOMAINS || '',
    PORT: '4205',
    SHINE_AI_BASE_URL: window.RUNTIME_CONFIG.SHINE_AI_API_URL || '',
    SHINE_ENRICH_BASE_URL: window.RUNTIME_CONFIG.SHINE_ENRICH_API_URL || '',
    SHINE_AI_WEB_URL: window.RUNTIME_CONFIG.SHINE_AI_WEB_URL || '',
      SHINE_CONNECT_API_URL:
          window.RUNTIME_CONFIG.SHINE_CONNECT_API_URL ||
          (window.location?.origin ? `${window.location.origin}/` : 'http://localhost:4205/'),
    SHINE_TRANSLATIONS_URL: window.RUNTIME_CONFIG.SHINE_TRANSLATIONS_URL || '',
      SHINE_WEBINARS_URL: window.RUNTIME_CONFIG.SHINE_WEBINARS_URL || 'http://localhost:3001/',
    SHINE_AUTH_EXTERNAL_URL: window.RUNTIME_CONFIG.SHINE_AUTH_EXTERNAL_URL || '',
    HOSTING_URL: window.RUNTIME_CONFIG.SHINE_UI_URL || '',
    WIDGET_SCRIPT_URL: window.RUNTIME_CONFIG.WIDGET_SCRIPT_URL ||
        (window.RUNTIME_CONFIG.SHINE_AI_FRONTEND_URL ? `${window.RUNTIME_CONFIG.SHINE_AI_FRONTEND_URL}generic.js?v=1.0.0` : ''),
    FRONTEND_DUMMY_URL: window.RUNTIME_CONFIG.FRONTEND_DUMMY_URL ||
        (window.RUNTIME_CONFIG.SHINE_AI_FRONTEND_URL ? `${window.RUNTIME_CONFIG.SHINE_AI_FRONTEND_URL}generic` : ''),
    LOG_LEVEL: window.RUNTIME_CONFIG.LOG_LEVEL || 'info',
    METRICS_ENABLED: window.RUNTIME_CONFIG.METRICS_ENABLED || 'true',
    TRACING_ENABLED: window.RUNTIME_CONFIG.TRACING_ENABLED || 'true',
    AZURE_AD_CLIENT_ID: window.RUNTIME_CONFIG.AZURE_AD_CLIENT_ID || '',
    AZURE_AD_AUTHORITY: window.RUNTIME_CONFIG.AZURE_AD_AUTHORITY || '',
  };
}

// Default configuration for development/testing only
// This should never be used in production as all config should come from config.js
function getDefaultConfig() {
  console.warn('Using default configuration - THIS SHOULD NOT HAPPEN IN PRODUCTION');

  return {
    NODE_ENV: 'development',
    PORT: '4205',
    SHINE_AI_BASE_URL: 'http://localhost:5002/',
    SHINE_ENRICH_BASE_URL: 'http://localhost:5001/',
    SHINE_AI_WEB_URL: 'http://localhost:5003/',
      SHINE_CONNECT_API_URL: 'http://localhost:4205/',
    SHINE_TRANSLATIONS_URL: 'http://localhost:5010/',
      SHINE_WEBINARS_URL: 'http://localhost:3001/',
      SHINE_AUTH_EXTERNAL_URL: 'http://localhost:3000/',
    HOSTING_URL: 'http://localhost:4205/',
    WIDGET_SCRIPT_URL: 'http://localhost:5004/generic.js?v=1.0.0',
    FRONTEND_DUMMY_URL: 'http://localhost:5004/generic',
    LOG_LEVEL: 'debug',
    METRICS_ENABLED: 'true',
    TRACING_ENABLED: 'true',
    AZURE_AD_CLIENT_ID: '',
    AZURE_AD_AUTHORITY: '',
  };
}

// Get configuration from runtime
const config = getRuntimeConfig();

// Validate configuration against the schema
const validatedConfig = envSchema.parse(config);

const isKarmaTest =
    typeof window !== 'undefined' &&
    typeof (window as Window & { __karma__?: unknown }).__karma__ !== 'undefined';

// Map the validated config to the Environment interface
export const environment: Environment = {
  production: config.NODE_ENV === 'production',
  environment: config.NODE_ENV,
  hostingUrl: validatedConfig.HOSTING_URL,
  aiBaseServerUrl: validatedConfig.SHINE_AI_BASE_URL,
    connectBaseServerUrl: validatedConfig.SHINE_CONNECT_API_URL,
  translationsBaseServerUrl: validatedConfig.SHINE_TRANSLATIONS_URL,
  enrichBaseServerUrl: validatedConfig.SHINE_ENRICH_BASE_URL,
  aiWebServerUrl: validatedConfig.SHINE_AI_WEB_URL,
    webinarsServerUrl: validatedConfig.SHINE_WEBINARS_URL,
  authBaseServerUrl: validatedConfig.SHINE_AUTH_EXTERNAL_URL,
  widgetScriptUrl: validatedConfig.WIDGET_SCRIPT_URL,
  frontendDummy: validatedConfig.FRONTEND_DUMMY_URL,
  logLevel: validatedConfig.LOG_LEVEL,
  metricsEnabled: validatedConfig.METRICS_ENABLED,
  tracingEnabled: validatedConfig.TRACING_ENABLED,
  msalEnabled: !isKarmaTest && validatedConfig.AUTHENTICATION_PROVIDER === 'entra',
  localClientPreparationEnabled: validatedConfig.LOCAL_CLIENT_PREPARATION_ENABLED,
  authenticationProvider: validatedConfig.AUTHENTICATION_PROVIDER,
  auth0Issuer: validatedConfig.AUTH0_ISSUER,
  auth0UiUrl: validatedConfig.AUTH0_UI_URL,
  auth0StaffConnection: validatedConfig.AUTH0_STAFF_CONNECTION,
  internalAdminEmailDomains: validatedConfig.INTERNAL_ADMIN_EMAIL_DOMAINS,
  azureAdClientId: validatedConfig.AZURE_AD_CLIENT_ID,
  azureAdAuthority: validatedConfig.AZURE_AD_AUTHORITY,
};
